// The phone's frame around the panel (the shell puts it there, always, when
// the host has the phone's screens; it draws its own pieces only at a phone's
// width).
//
// Promise:
// - `menu: "dock"`: the Dock floats at the bottom. What it shows:
//   1. a page that put nothing or one button on it (a list's "+", a Case's
//      chat): the left slot (that button, or empty) and the Dock itself (Me's
//      entries | the place's | "⋯"). The slot is always kept, so the Dock
//      has the same length on every screen (the person, 2026-10-18: a Dock
//      whose length changed by screen was disliked);
//   2. a page that put its tools on it (a document's undo, redo, outline):
//      the tools, and the Dock folded to the round button at the right;
//   3. a page that put its post box on it: the box stands right above the
//      Dock, which stays as in 1 (the person, 2026-10-28: the box apart from
//      the Dock, always above it).
//   Which one is decided by what the page holds, never by measuring the
//   screen (measuring made it open and close on the same screen). The way
//   back is not on the Dock: it is the header's, at the top left.
// - The Dock grown from the round button has the resting Dock's length.
// - The round button and "⋯" open the menu (menu.tsx).
// - While a text field outside the Dock has the keyboard, the Dock sinks away
//   and cannot be touched (a fixed bar goes under the keyboard on scroll).
// - While something over the screen asks it to (`stow`: a drawer that takes
//   the bottom of the screen), the Dock and a page's post box row sink away the
//   same way, keeping their room (the chat behind does not move).
// - `menu: "settings"`: the Dock as with `dock` (the settings' index lists
//   their pages; an item's page goes up to it from the left slot).
// - `menu: "drawer"` (the admin's pages): a round button at the bottom right
//   opens the frame's own drawer.
// - The frame is kept on the visible part of the screen (keyboard.ts) and
//   lists keep room at their end for the Dock (`--dock-clearance`, phone.css).
import { useRouter, useRouterState } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../app/context";
import { useDeviceSetting } from "../device/device-setting";
import { SHELL_CONTRACT_VERSION } from "../shell/outside/contract";
import { useShellMenu } from "../shell/outside/source";
import {
  type DockUpProps,
  HostStowContext,
  HostStowRelay,
  type PhoneFrameProps,
} from "../shell/phone";
import { GLASS } from "../ui/glass";
import { Icon } from "../ui/icon";
import { type MenuState, PhoneContext, type PhoneState } from "./context";
import {
  DockRow,
  DockSurface,
  DockUpButton,
  RoundMenuButton,
  useHere,
} from "./dock-row";
import {
  arrangeDock,
  DOCK_GAP,
  DOCK_ITEM_WIDTH,
  DOCK_SIDE,
  dockEntries,
  dockPrefsSetting,
  fitCapacity,
} from "./items";
import { useKeyboardFrame } from "./keyboard";
import { DOCK_BOTTOM, useFrameWidth } from "./layout";
import { MenuLayer } from "./menu";
import { useTapTopToScroll } from "./tap-top";
import { useTypingIn } from "./typing";

/** Comes in swelling from three quarters and clear (the old web's way back). */
const SWELL_IN =
  "flex transition-[opacity,scale] duration-200 ease-out starting:scale-75 starting:opacity-0 motion-reduce:transition-none";

let upSeq = 0;

/** Inside the Dock (its row, a page's post box row, the menu): not "elsewhere". */
const IN_DOCK = "[data-phone-dock],[data-phone-input],[data-phone-menu]";

export function PhoneFrame({
  active,
  menu,
  openDrawer,
  drawerOpen,
  hostDraws = false,
  children,
}: PhoneFrameProps) {
  const router = useRouter();
  const shell = useShellMenu();
  const place = shell.place;
  const [prefs] = useDeviceSetting(dockPrefsSetting);
  const frameWidth = useFrameWidth();
  const on = active && menu !== "drawer";
  // The Dock and its menu, unless the host draws them (it keeps their room).
  const drawn = on && !hostDraws;
  const [claims, setClaims] = useState({ cluster: 0, tools: 0, input: 0 });
  // `bar`: of them, the ones that hand the app's bar a header of their own
  // (a dialog filling the screen): the bar stays, only the Dock goes.
  const [stows, setStows] = useState({ all: 0, bar: 0 });
  const [clusterHost, setClusterHost] = useState<HTMLElement | null>(null);
  const [toolsHost, setToolsHost] = useState<HTMLElement | null>(null);
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);
  const [ups, setUps] = useState<
    readonly { id: number; up: DockUpProps; strong: boolean }[]
  >([]);
  const [menuState, setMenuState] = useState<MenuState>(null);
  /**
   * The menu just closed, kept drawn while it folds back (menu.tsx), and a
   * count of openings (a new opening draws a new layer).
   */
  const [leaving, setLeaving] = useState<MenuState>(null);
  const [opening, setOpening] = useState(0);
  const menuNow = useRef<MenuState>(null);
  menuNow.current = menuState;
  const menuButton = useRef<HTMLButtonElement>(null);
  // The buttons that open the menu, in the order they came (context.tsx
  // `holdMenuButton`): the ref is the last one still there.
  const menuButtons = useRef(new Set<HTMLButtonElement>());
  const holdMenuButton = useCallback((el: HTMLButtonElement | null) => {
    if (!el) return;
    menuButtons.current.add(el);
    menuButton.current = el;
    return () => {
      menuButtons.current.delete(el);
      if (menuButton.current === el)
        menuButton.current = [...menuButtons.current].at(-1) ?? null;
    };
  }, []);
  // The screen shown (its confirmed match): going elsewhere closes the menu.
  const path = useRouterState({ select: (s) => s.matches.at(-1)?.pathname });
  if (menuState !== null && (!on || menuState.path !== path)) {
    setLeaving(on ? menuState : null);
    setMenuState(null);
  }

  const away = useTypingIn((el) => el.closest(IN_DOCK) === null, on);
  useKeyboardFrame(active, router);
  useTapTopToScroll(active);

  const { me, place: theirs } = dockEntries(shell);
  const inputHeld = claims.input > 0;
  // The page's tools fold the Dock to its round button.
  const folded = !inputHeld && claims.tools > 0;
  const arrange = useCallback(
    (capacity: number) => arrangeDock(me, theirs, prefs, capacity),
    [me, theirs, prefs],
  );
  // The Dock's room: the frame less its sides and the left slot, which is
  // always kept (empty or not), so the Dock has the same length on every
  // screen (the person, 2026-10-18).
  const lane = frameWidth - 2 * DOCK_SIDE - (DOCK_ITEM_WIDTH + DOCK_GAP);
  const arranged = arrange(fitCapacity(lane, arrange));
  const here = useHere(arranged.all);

  const claim = useCallback((kind: "cluster" | "tools" | "input") => {
    setClaims((c) => ({ ...c, [kind]: c[kind] + 1 }));
    return () => setClaims((c) => ({ ...c, [kind]: c[kind] - 1 }));
  }, []);
  const stow = useCallback((keepBar?: boolean) => {
    const bar = keepBar ? 1 : 0;
    setStows((n) => ({ all: n.all + 1, bar: n.bar + bar }));
    return () => setStows((n) => ({ all: n.all - 1, bar: n.bar - bar }));
  }, []);
  // The layers beside the screens (a confirmation) reach it through the relay.
  const relay = useContext(HostStowRelay);
  // frontend-development: not a fetch; lends `stow` to those layers.
  useLayoutEffect(() => {
    if (!relay || !hostDraws) return;
    relay.current = stow;
    return () => {
      if (relay.current === stow) relay.current = null;
    };
  }, [relay, hostDraws, stow]);
  const stowed = on && stows.all > 0;
  // What the app draws over the top goes too, unless all that covers keeps it.
  const covered = stowed && stows.all > stows.bar;
  // The way up: what a page names (strong) wins over a list's way back to
  // itself; among the same, the last one.
  const pushUp = useCallback((up: DockUpProps) => {
    const id = ++upSeq;
    setUps((u) => [...u, { id, up, strong: up.strong === true }]);
    return () => setUps((u) => u.filter((x) => x.id !== id));
  }, []);
  const up = on
    ? ((ups.filter((u) => u.strong).at(-1) ?? ups.at(-1))?.up ?? null)
    : null;
  // A post box stands over the Dock (dock-slot.tsx). While a field in it has
  // the focus the Dock sinks away and the box comes down onto the keyboard
  // (phone.css: the box's room and the Dock follow the same focus).
  const hidden = away || stowed;
  // The host's Dock sinks away when this one would (the outside's `dock`).
  const host = useMeApp().extensions.shellHost;
  // frontend-development: not a fetch; tells the host drawing the Dock.
  useEffect(() => {
    if (hostDraws && active)
      host?.post({
        version: SHELL_CONTRACT_VERSION,
        type: "dock",
        hidden,
        covered,
      });
  }, [host, hostDraws, active, hidden, covered]);
  const dockRef = useRef<HTMLDivElement>(null);
  const openMenu = useCallback(
    (
      from: "button" | "resting",
      button: HTMLElement,
      panel: boolean,
      pointer: boolean,
    ) => {
      // The surface lies over the resting Dock, or grows from the round
      // button to the left as long as the resting Dock is (the same Dock on
      // every screen), its right edge at the button's.
      // Where the Dock's row is by the same rules that place the row (its
      // sides and its height above the bottom), in the frame that holds the
      // fixed pieces, not measured against the window: on iOS 26 the window
      // runs on under Safari's floating bar, and the Dock grown from the
      // round button came out below its row and off to the left (2026-10-18,
      // on the person's iPhone). Its length is the frame's less the sides and
      // the left slot (the resting Dock's).
      const frame = button.closest("[data-me-frame]") ?? document.body;
      setLeaving(null);
      setOpening((n) => n + 1);
      setMenuState({
        from,
        opener: button,
        panel,
        pointer,
        path,
        box: {
          right: DOCK_SIDE,
          bottom: DOCK_BOTTOM,
          width: Math.round(
            frame.getBoundingClientRect().width -
              2 * DOCK_SIDE -
              (DOCK_ITEM_WIDTH + DOCK_GAP),
          ),
        },
      });
    },
    [path],
  );
  // Closing keeps what was open drawn while it folds back (menu.tsx).
  const closeMenu = useCallback(() => {
    if (!menuNow.current) return;
    setLeaving(menuNow.current);
    setMenuState(null);
  }, []);
  const togglePanel = useCallback(() => {
    const s = menuNow.current;
    if (!s) return;
    // Closing the panel closes the menu (the Dock opened from the round
    // button folds back too).
    if (s.panel) closeMenu();
    else setMenuState({ ...s, panel: true });
  }, [closeMenu]);
  const exited = useCallback(() => setLeaving(null), []);

  // What phone.css lays out by: the floating Dock (`dock`; lists keep room
  // for it at their end), the same over the settings' pages (`settings`), a
  // page's post box holding the Dock's row (`input`), or the round button
  // that opens the drawer (`drawer`).
  const layout = !active
    ? null
    : menu === "drawer"
      ? "drawer"
      : inputHeld
        ? "input"
        : menu;
  // frontend-development: not a fetch; marks the page for phone.css. The mark
  // is changed in place and taken off only when the frame goes: taken off
  // between two values, a measuring effect in between styled a newly opened
  // post box without the phone's rules, and its row then rose from 16 px to
  // the phone's height above the home bar (it came up from below).
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (layout) html.setAttribute("data-phone-layout", layout);
    else html.removeAttribute("data-phone-layout");
  }, [layout]);
  // frontend-development: not a fetch; the mark's removal with the frame.
  useLayoutEffect(
    () => () => document.documentElement.removeAttribute("data-phone-layout"),
    [],
  );
  // frontend-development: not a fetch; marks the page for phone.css (the top
  // Dock's buttons are the host's: theirs keep the room, not shown).
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (active && hostDraws) html.setAttribute("data-phone-host", "");
    else html.removeAttribute("data-phone-host");
    return () => html.removeAttribute("data-phone-host");
  }, [active, hostDraws]);

  const state: PhoneState = {
    active,
    hostDraws,
    menu,
    claims,
    clusterHost,
    toolsHost,
    up,
    pushUp,
    menuState,
    away,
    stowed,
    all: arranged.all,
    here,
    menuButton,
    holdMenuButton,
    overlayHost,
    claim,
    stow,
    openMenu,
    togglePanel,
    closeMenu,
  };
  return (
    <PhoneContext.Provider value={state}>
      <HostStowContext.Provider value={hostDraws ? stow : null}>
        {children}
      </HostStowContext.Provider>
      {drawn && (
        <div
          ref={dockRef}
          data-phone-dock=""
          inert={hidden || undefined}
          style={{ bottom: DOCK_BOTTOM }}
          className={`pointer-events-none fixed inset-x-0 z-30 flex items-end transition-[opacity,translate] duration-200 ease-out motion-reduce:transition-none ${
            hidden
              ? "invisible translate-y-3 opacity-0"
              : "translate-y-0 opacity-100"
          }`}
        >
          {/* The sides (33) are the old web's Dock's: Safari's own bottom bar. */}
          <div className="flex min-w-0 flex-1 items-end px-[33px]">
            {/* The left slot: a list's "+", else the way up to the parent,
                else empty. Always kept, so the Dock never changes its length. */}
            {/* What comes into it swells in from a smaller, clear form (the
                old web's way back: scale and fade, 200 ms); the slot's size
                never changes. */}
            <div className="mr-[7px] flex h-12 w-[49px] flex-none">
              {claims.cluster > 0 ? (
                <div key="add" className={SWELL_IN}>
                  <PageSlot hostRef={setClusterHost} />
                </div>
              ) : (
                up && (
                  <div key={`up:${up.to}`} className={SWELL_IN}>
                    <DockUpButton to={up.to} label={up.label} />
                  </div>
                )
              )}
            </div>
            {folded ? (
              <>
                {/* Squeezed by the Dock grown from the round button (menu.tsx);
                    no CSS transition on its opacity: it would win over that
                    move. */}
                <DockSurface
                  data-phone-squeeze="width"
                  inert={menuState?.from === "button" || undefined}
                  className={`h-12 flex-none px-1 ${
                    menuState?.from === "button" ? "opacity-0" : ""
                  }`}
                >
                  <div ref={setToolsHost} className="flex items-center" />
                </DockSurface>
                <div className="flex-1" />
                <div className="ml-[7px] flex-none">
                  <RoundMenuButton
                    ref={holdMenuButton}
                    here={here}
                    others={arranged.all.filter((e) => e.key !== here?.key)}
                    open={menuState !== null}
                    onOpen={(p, button) => openMenu("button", button, false, p)}
                  />
                </div>
              </>
            ) : (
              <DockSurface
                data-phone-resting=""
                className="h-12 min-w-0 flex-1"
                // The menu's copy lies over it while open, and while it folds
                // back: shown under the folding copy, the two glasses and
                // their shadows lay one over the other, then the copy went
                // and the shadow under the row flickered (the person,
                // 2026-10-18; the old web kept one surface until the end).
                style={
                  (menuState ?? leaving)?.from === "resting"
                    ? { visibility: "hidden" }
                    : undefined
                }
              >
                <DockRow
                  dock={arranged.dock}
                  all={arranged.all}
                  here={here}
                  panel={false}
                  moreRef={holdMenuButton}
                  onMore={(p, button) => openMenu("resting", button, true, p)}
                />
              </DockSurface>
            )}
          </div>
        </div>
      )}
      {drawn && (menuState ?? leaving) && (
        <MenuLayer
          key={opening}
          menu={(menuState ?? leaving) as NonNullable<MenuState>}
          closing={menuState === null}
          onExited={exited}
          arrange={arrange}
          place={place}
        />
      )}
      {/* What lies over the whole screen (overlay.tsx). Its boxes are placed
          by the frame itself (the shell's grid is not a positioned box), so
          they keep to the visible part of the screen. */}
      {active && <div ref={setOverlayHost} className="contents" />}
      {/* For a screen without a header row in the home screen app: the
          surface at the top edge that keeps iOS from blurring it. It has the
          panel's color but paints nothing (phone.css; shown only there). */}
      {active && (
        <div
          aria-hidden="true"
          data-phone-top-edge=""
          className="pointer-events-none fixed inset-x-0 top-0 z-[29] hidden h-3 bg-surface"
        />
      )}
      {active && !hostDraws && menu === "drawer" && (
        // Fixed on a wrapper: glass is `position: relative` (styles).
        <div
          className="fixed z-20"
          style={{ bottom: DOCK_BOTTOM, right: DOCK_SIDE }}
        >
          <button
            type="button"
            aria-label={m.shell_open_sidebar()}
            aria-expanded={drawerOpen}
            onMouseDown={(e) => e.preventDefault()}
            onClick={openDrawer}
            className={`${GLASS} glass-icon flex h-12 w-[49px] cursor-pointer items-center justify-center rounded-[24px] text-fg`}
          >
            <Icon kind="panel" size={18} />
          </button>
        </div>
      )}
    </PhoneContext.Provider>
  );
}

/**
 * The left slot's surface: the page's one round button (a list's "+") is drawn
 * into it from the page (a portal).
 */
function PageSlot({ hostRef }: { hostRef(el: HTMLElement | null): void }) {
  return (
    <DockSurface className="h-12 w-[49px] flex-none">
      <div ref={hostRef} className="flex items-center" />
    </DockSurface>
  );
}
